import { User } from '../../user';
import { map, isString, isArray, every, isNumber } from 'lodash';
import JSBI from 'jsbi';
import { SLEntity, SLAddress, SLEntityType, SLEntityField } from './entity';
import { lessThanEZero } from './utils';

export type SLMovementLineOwnership = 'PP' | 'NP' | 'UF' | 'AT';

const WRITING_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export function slMovementLineOwnerships(): SLMovementLineOwnership[] {
  return ['PP', 'NP', 'UF', 'AT'];
}

export type SLMovementLineShareType = 'ORD' | 'PREF' | 'BSA' | 'BSPCE' | 'AGA' | 'SO';

export function slMovementLineShareTypes(): SLMovementLineShareType[] {
  return ['ORD', 'PREF', 'BSA', 'BSPCE', 'AGA', 'SO'];
}

export type SLMovementLineOperation = 'CREDIT' | 'DEBIT';

export type SLMovementLineNature = 'TRANSFER' | 'SUBSCRIPTION' | 'CANCELLATION' | 'OTHER';

export const SLMovementLineNatureByOperation = {
  CREDIT: ['TRANSFER', 'SUBSCRIPTION', 'OTHER'] as SLMovementLineNature[],
  DEBIT: ['TRANSFER', 'CANCELLATION', 'OTHER'] as SLMovementLineNature[],
};

export type Share = JSBI;

export class SLMovementLineEntity {
  id: string
  version: number
  name: string
  address: SLAddress
  type: SLEntityType
  extraFields: SLEntityField[]

  constructor(data: any = {}) {
    Object.assign(this, data);

    if (this.address) {
      this.address = new SLAddress(this.address);
    }
  }
}

export class SLMovementLine {
  operation: SLMovementLineOperation
  shareType: SLMovementLineShareType
  ownership: SLMovementLineOwnership
  nature: SLMovementLineNature
  amount: Share
  isHidden: boolean
  entity: SLMovementLineEntity

  constructor(data: any = {}) {
    Object.assign(this, data);

    if (isString(data.amount)) {
      this.amount = JSBI.BigInt(this.amount);
    }

    if (this.entity) {
      this.entity = new SLMovementLineEntity(this.entity);
    }
  }

  get valid():boolean {
    // Entity
    if (!this.entity) {
      return false;
    }

    // Amount
    if (this.amount === null || this.amount === undefined) {
      return false;
    }

    if (lessThanEZero(this.amount)) {
      return false;
    }

    return true;
  }
}

export class SLMovementAttachment {
  id: string
  filename: string
  filetype: string
  filesize: number
  digest: string
  createdAt: number

  constructor(data: any = {}) {
    Object.assign(this, data);
  }

  get valid():boolean {
    // Digest
    if (!this.digest) {
      return false;
    }

    return true;
  }
}

export class SLMovement {
  id: string
  writingDate: string
  description: string
  lines: SLMovementLine[]
  attachments: SLMovementAttachment[]
  createdAt: number
  createdBy: User

  constructor(data: any = {}) {
    Object.assign(this, data);

    if(this.lines) {
      this.lines = map(this.lines, l => new SLMovementLine(l));
    }

    if(this.attachments) {
      this.attachments = map(this.attachments, a => new SLMovementAttachment(a));
    }

    if(this.createdBy) {
      this.createdBy = new User(this.createdBy);
    }
  }

  get valid():boolean {

    // writingDate
    if (!this.writingDate || !isString(this.writingDate) || !WRITING_DATE_REGEX.test(this.writingDate)) {
      return false;
    }

    // description
    if(!isString(this.description)) {
      return false;
    }

    let trimmedDescription = this.description.trim();

    if(trimmedDescription.length > 200) {
      return false;
    }

    if(trimmedDescription.length === 0) {
      return false;
    }


    // lines
    if (!isArray(this.lines)) {
      return false;
    }

    if (this.lines.length < 1) {
      return false;
    }

    const linesValid = every(this.lines, l => l.valid);

    if (!linesValid) {
      return false;
    }


    // attachments
    if (this.attachments && isArray(this.attachments) && this.attachments.length > 0) {
      const attachmentsValid = every(this.attachments, a => a.valid);

      if (!attachmentsValid) {
        return false;
      }
    }
    

    return true;
  }

  toCreateSLMovementInput(entity: SLEntity, draftMovementId: string): CreateSLMovementInput {
    const writingDate = new Date(this.writingDate);

    return {
      entityId: entity.id,
      entityVersion: entity.version,
      writingDate: {
        year: writingDate.getFullYear(),
        month: writingDate.getMonth() + 1,
        day: writingDate.getDate(),
      },
      description: this.description,
      lines: map(this.lines, (l):SLMovementLineInput => ({
        operation: l.operation,
        shareType: l.shareType,
        ownership: l.ownership,
        nature: l.nature,
        amount: l.amount.toString(10),
        isHidden: l.isHidden,
        entityId: l.entity.id,
        entityVersion: l.entity.version,
      })),
      attachmentsIds: map(this.attachments, a => a.id),
      draftMovementId,
    };
  }
}


// Input

export interface SLMovementLineInput {
  operation: SLMovementLineOperation
  shareType: SLMovementLineShareType
  ownership: SLMovementLineOwnership
  nature: SLMovementLineNature
  amount: string
  isHidden: boolean
  entityId: string
  entityVersion: number
}

export interface CreateSLMovementInput {
  entityId: string
  entityVersion: number
  writingDate: {
    year: number
    month: number
    day: number
  },
  description: string
  lines: SLMovementLineInput[]
  attachmentsIds: string[]
  draftMovementId?: string
}

// Deleted Movements
export class SLDeletedMovement extends SLMovement {
  deletedAt: number
  deletedBy: User

  constructor(data: any = {}) {
    super(data);

    if(this.deletedBy) {
      this.deletedBy = new User(this.deletedBy);
    }
  }
}
